import React from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode"; // Import jwt-decode to handle JWT expiry
import { BACKEND_BASE_URL } from "../config";

const UserStateContext = React.createContext();
const UserDispatchContext = React.createContext();

function userReducer(state, action) {
  switch (action.type) {
    case "LOGIN_SUCCESS":
      return { ...state, isAuthenticated: true, user: action.payload };
    case "SIGN_OUT_SUCCESS":
      return { ...state, isAuthenticated: false, user: null };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

function UserProvider({ children }) {
  const [state, dispatch] = React.useReducer(userReducer, {
    isAuthenticated: !!localStorage.getItem("authToken"), // Checks for token presence in localStorage
    user: null, // Optional user data
  });

  // Check if the token is expired on app load
  React.useEffect(() => {
    // const token = localStorage.getItem("authToken");
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const expirationTime = decoded.exp * 1000; // Expiry in milliseconds
        if (expirationTime > new Date().getTime()) {
          dispatch({ type: "LOGIN_SUCCESS", payload: decoded });
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        // Only clear if token decoding failed
        localStorage.removeItem("authToken");
        dispatch({ type: "SIGN_OUT_SUCCESS" });
      }
    }
  }, []);

  return (
    <UserStateContext.Provider value={state}>
      <UserDispatchContext.Provider value={dispatch}>
        {children}
      </UserDispatchContext.Provider>
    </UserStateContext.Provider>
  );
}

// Axios instance with interceptors
const api = axios.create({
  baseURL: `${BACKEND_BASE_URL}/api/`,
});

// Request Interceptor - Adds Access Token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor - Handles Token Expiry (401)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      try {
        await refreshAccessToken();
        return api(error.config); // Retry failed request
      } catch (refreshError) {
        console.error("Token refresh failed", refreshError);
        signOut();
      }
    }
    return Promise.reject(error);
  }
);

// Function to Refresh Access Token
async function refreshAccessToken(dispatch) {
  try {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) throw new Error("No refresh token available");

    const { data } = await axios.post(`${BACKEND_BASE_URL}/api/token/refresh/`, {
      refresh: refreshToken,
    });

    // Save new tokens
    localStorage.setItem("authToken", data.access);
    localStorage.setItem("refreshToken", data.refresh);

    // Decode token and update state
    const decoded = jwtDecode(data.access);
    dispatch({ type: "LOGIN_SUCCESS", payload: decoded });
  } catch (error) {
    console.error("Error refreshing token:", error);
    signOut(dispatch);
  }
}


function useUserState() {
  const context = React.useContext(UserStateContext);
  if (context === undefined) {
    throw new Error("useUserState must be used within a UserProvider");
  }
  return context;
}

function useUserDispatch() {
  const context = React.useContext(UserDispatchContext);
  if (context === undefined) {
    throw new Error("useUserDispatch must be used within a UserProvider");
  }
  return context;
}




async function loginUser(dispatch, loginValue, passwordValue, history, setIsLoading, setError) {
  setIsLoading(true);
  setError(null); // Reset errors on new attempt
  try {
    const response = await axios.post(`${BACKEND_BASE_URL}/api/admin-login/`, {
      email: loginValue,
      password: passwordValue,
    });

    const { access, refresh, user } = response.data;

    // 🔐 Save tokens locally
    localStorage.setItem("authToken", access);
    localStorage.setItem("refreshToken", refresh);
    localStorage.setItem("user", JSON.stringify(user));

    // ✅ Set Authorization header globally
    axios.defaults.headers.common["Authorization"] = `Bearer ${access}`;

    // 📢 Dispatch to Redux or context if needed
    dispatch({ type: "LOGIN_SUCCESS", payload: user });

    // 🔀 Navigate based on user role
    if (user.role === "DEMO") {
      history.push("/app/demo-access");
    } else if (user.role === "BULK_IMPORT") {
      history.push("/app/upload");
    } else if (user.is_staff || user.is_superuser) {
      history.push("/app/manage");
    } else {
      history.push("/app/manage");
    }

  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      "Login failed. Please try again.";

    setError(errorMessage);
  } finally {
    setIsLoading(false);
  }


}


const registerUser = async (nameValue, loginValue, passwordValue, setActiveTabId, setIsLoading, setError) => {
  setIsLoading(true);
  try {
    await axios.post(`${BACKEND_BASE_URL}/api/register`, {
      name: nameValue,
      email: loginValue,
      password: passwordValue,
    });
    alert("Registration successful! You can now login.");
    setActiveTabId(0); // Switch back to the login tab
  } catch (error) {
    setError("Registration error. Please check your inputs.");
  } finally {
    setIsLoading(false);
  }
};

function signOut(dispatch, history) {
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
  dispatch({ type: "SIGN_OUT_SUCCESS" });
  history.push("/login");
}


export { UserProvider, useUserState, useUserDispatch, loginUser, signOut, registerUser };